import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

const DEFAULT_AVATAR =
  "https://st4.depositphotos.com/6672868/20448/v/600/depositphotos_204485038-stock-illustration-user-vector-icon-simple-illustration.jpg";

export const getAllImages = createAsyncThunk(
  "images/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`image/getAllImage`, { params });
      return {
        data: response.data.data,
        total: response.data.total,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch data"
      );
    }
  }
);

export const addImageComment = createAsyncThunk(
  "images/addImageComment",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const response = await axiosClient.post(`/image/addImageComment`, {
        imageId: params.imageId,
        Comment: params.Comment,
        commentCreatedBy: params.commentCreatedBy,
      });
      return { ...response.data.data, tempId: params.tempId }; // Include tempId for rollback
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to add comment",
        tempId: params.tempId, // Pass tempId for rollback
      });
    }
  }
);

export const fetchComments = createAsyncThunk(
  "images/fetchComments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/image/imageCommentList`, {
        params,
      });
      return {
        data: response.data.data.map((item) => ({
          id: item.imageId,
          image: item.imageUrl,
          latestComment: item.latestComment || "No comment",
          totalComments: item.totalComments,
          pendingCount: item.pendingCount,
          approvedCount: item.approvedCount,
          rejectedCount: item.rejectedCount,
          latestCommentAt: item.latestCommentAt,
          latestCommentBy: item.latestCommentBy,
          commentId: item.commentId,
        })),
        totalCount: response.data.totalCount,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch comments"
      );
    }
  }
);

export const fetchCommentsById = createAsyncThunk(
  "images/fetchCommentsById",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/image/imageCommentListByImageId`,
        {
          params: {
            imageId: params.imageId,
            commentStatus: params.commentStatus,
            userId: params.userId,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch comments by ID"
      );
    }
  }
);

export const updateCommentStatus = createAsyncThunk(
  "images/updateCommentStatus",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { commentStatus, originalStatus } = params;
      const response = await axiosClient.post(`/image/updateImageComment`, {
        imageId: params.imageId,
        commentId: params.commentId,
        commentStatus,
      });
      return {
        ...response.data.data,
        commentId: params.commentId,
        commentStatus,
        originalStatus,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update comment status"
      );
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "media/fetchNotifications",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/image/notificationList", {
        params,
      });
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch notifications"
        );
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

const initialState = {
  allMedia: [],
  isMediaLoading: false,
  mediaError: null,
  totalMediaCount: 0,
  mediaPageId: 1,
  mediaSearch: "",
  mediaType: 0,
  title: "",
  description: "",
  selectedServiceOrNeed: null,
  boost: 0,
  likesCount: 0,
  clickCount: 0,
  selectedCat: null,
  tasks: [],
  comments: [],
  imageComments: [],
  isCommentLoading: false,
  commentError: null,
  clickedImageId: null,
  entertainment: null,
  keywords: null,
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  hasMoreNotifications: false,
  pendingRequests: [],
  totalCommentsCount: 0,
  categoryType: "category",
};

const mediaSlice = createSlice({
  name: "media",
  initialState,
  reducers: {
    reset: (state) => {
      state.mediaType = 0;
      state.title = "";
      state.description = "";
      state.selectedServiceOrNeed = null;
      state.boost = 0;
      state.likesCount = 0;
      state.clickCount = 0;
      state.selectedCat = null;
      state.entertainment = "";
      state.keywords = "";
      state.categoryType = "category";
    },
    setAllMedia: (state, action) => {
      state.allMedia = action.payload;
    },
    setMediaPageId: (state, action) => {
      state.mediaPageId = action.payload;
    },
    setMediaSearch: (state, action) => {
      state.mediaSearch = action.payload;
    },
    setMediaType: (state, action) => {
      state.mediaType = action.payload;
    },
    setTitle: (state, action) => {
      state.title = action.payload;
    },
    setDescription: (state, action) => {
      state.description = action.payload;
    },
    setSelectedServiceOrNeed: (state, action) => {
      state.selectedServiceOrNeed = action.payload;
    },
    setBoost: (state, action) => {
      state.boost = action.payload;
    },
    setLikesCount: (state, action) => {
      state.likesCount = action.payload;
    },
    setClickCount: (state, action) => {
      state.clickCount = action.payload;
    },
    setSelectedCat: (state, action) => {
      state.selectedCat = action.payload;
    },
    setClickedImageId: (state, action) => {
      state.clickedImageId = action.payload;
    },
    clearClickedImageId: (state) => {
      state.clickedImageId = null;
    },
    setEntertainment: (state, action) => {
      state.entertainment = action.payload;
    },
    setKeywords: (state, action) => {
      state.keywords = action.payload;
    },
    setCategoryType: (state, action) => {
      state.categoryType = action.payload;
    },
    addUploadTask(state, action) {
      state.tasks.push({
        id: action.payload.id,
        progress: 0,
        title: action.payload.title || "Uploading files",
      });
    },
    setUploadProgress(state, action) {
      const { id, progress } = action.payload;
      const task = state.tasks.find((t) => t.id === id);
      if (task) {
        task.progress = progress;
      }
    },
    removeUploadTask(state, action) {
      state.tasks = state.tasks.filter((t) => t.id !== action.payload);
    },
    clearUploadTasks(state) {
      state.tasks = [];
    },
    resetAllImagesData: (state) => {
      state.allMedia = [];
      state.totalMediaCount = 0;
      state.mediaPageId = 1;
      state.mediaError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllImages.pending, (state, action) => {
        const requestKey = JSON.stringify(action.meta.arg);
        if (!state.pendingRequests.includes(requestKey)) {
          state.isMediaLoading = true;
          state.pendingRequests.push(requestKey);
        }
      })
      .addCase(getAllImages.fulfilled, (state, action) => {
        state.isMediaLoading = false;
        const requestKey = JSON.stringify(action.meta.arg);
        state.pendingRequests = state.pendingRequests.filter(
          (request) => request !== requestKey
        );

        const pageId = action.meta.arg.pageId || 1;
        if (pageId === 1) {
          state.allMedia = action.payload.data;
        } else {
          const existingIds = new Set(state.allMedia.map((item) => item.id));
          const newData = action.payload.data.filter(
            (item) => !existingIds.has(item.id)
          );
          state.allMedia = [...state.allMedia, ...newData];
        }
        state.totalMediaCount = action.payload.total;
        state.mediaError = null;
      })
      .addCase(getAllImages.rejected, (state, action) => {
        state.isMediaLoading = false;
        const requestKey = JSON.stringify(action.meta.arg);
        state.pendingRequests = state.pendingRequests.filter(
          (request) => request !== requestKey
        );
        state.mediaError = action.payload;
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        const { pageId, pageSize = 10 } = action.meta.arg;
        const newData = action.payload.data || [];
        if (pageId > 1) {
          state.notifications = [...state.notifications, ...newData];
        } else {
          state.notifications = newData;
        }
        state.hasMoreNotifications = newData.length === pageSize;
        state.notificationsError = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError = action.payload;
      })
      .addCase(addImageComment.pending, (state, action) => {
        state.isCommentLoading = true;
        state.commentError = null;
        const {
          imageId,
          Comment,
          commentCreatedBy,
          tempId,
          userName,
          imageUrl,
        } = action.meta.arg;
        state.imageComments.unshift({
          id: tempId,
          imageId,
          comment: Comment,
          userName: userName || "Anonymous",
          userPhoto: imageUrl || DEFAULT_AVATAR,
          createdAt: new Date().toISOString(),
          commentStatus: 0,
          commentCreatedBy: commentCreatedBy,
        });
      })

      .addCase(addImageComment.fulfilled, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = null;
        const { tempId } = action.meta.arg;
        const payload = action.payload;
        const tempCommentIndex = state.imageComments.findIndex(
          (comment) => comment.id === tempId
        );
        if (tempCommentIndex !== -1) {
          state.imageComments[tempCommentIndex] = {
            ...state.imageComments[tempCommentIndex],
            ...payload,
            id: payload.id,
          };
        }
      })
      .addCase(addImageComment.rejected, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = action.payload.message;
        state.imageComments = state.imageComments.filter(
          (comment) => comment.id !== action.payload.tempId
        );
      })
      .addCase(fetchComments.pending, (state) => {
        state.isCommentLoading = true;
        state.commentError = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.isCommentLoading = false;
        state.comments = action.payload.data;
        state.totalCommentsCount = action.payload.totalCount;
        state.commentError = null;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = action.payload;
      })
      .addCase(fetchCommentsById.pending, (state) => {
        state.isCommentLoading = true;
        state.commentError = null;
      })

      .addCase(fetchCommentsById.fulfilled, (state, action) => {
        state.isCommentLoading = false;
        state.imageComments = action.payload.map((comment) => ({
          ...comment,
          userPhoto: comment.userPhoto || comment.imageUrl || DEFAULT_AVATAR,
        }));
        state.commentError = null;
      })
      .addCase(fetchCommentsById.rejected, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = action.payload;
      })

      .addCase(updateCommentStatus.pending, (state, action) => {
        state.isCommentLoading = true;
        state.commentError = null;
        const { commentId, commentStatus } = action.meta.arg;
        const ids = Array.isArray(commentId) ? commentId : [commentId];
        if (commentStatus === 3) {
          state.imageComments = state.imageComments.filter(
            (item) => !ids.includes(item.id)
          );
        } else {
          state.imageComments = state.imageComments.map((item) =>
            ids.includes(item.id) ? { ...item, commentStatus } : item
          );
        }
      })
      .addCase(updateCommentStatus.fulfilled, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = null;
      })
      .addCase(updateCommentStatus.rejected, (state, action) => {
        state.isCommentLoading = false;
        state.commentError = action.payload;
        const {
          commentId,
          commentStatus,
          originalStatus = 0,
        } = action.meta.arg;
        const ids = Array.isArray(commentId) ? commentId : [commentId];
        if (commentStatus === 3) {
          state.imageComments = state.imageComments.map((item) =>
            ids.includes(item.id)
              ? { ...item, commentStatus: originalStatus }
              : item
          );
        } else {
          state.imageComments = state.imageComments.map((item) =>
            ids.includes(item.id)
              ? { ...item, commentStatus: originalStatus }
              : item
          );
        }
      });
  },
});

export const {
  reset,
  setAllMedia,
  setMediaPageId,
  setMediaSearch,
  setMediaType,
  setTitle,
  setDescription,
  setSelectedServiceOrNeed,
  setBoost,
  setLikesCount,
  setClickCount,
  setSelectedCat,
  setClickedImageId,
  clearClickedImageId,
  addUploadTask,
  setUploadProgress,
  removeUploadTask,
  clearUploadTasks,
  setEntertainment,
  setKeywords,
  setCategoryType,
  resetAllImagesData,
} = mediaSlice.actions;

export default mediaSlice.reducer;

export const getMediaData = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    const requestKey = JSON.stringify(params);
    if (
      state.media.pendingRequests.includes(requestKey) ||
      state.media.isMediaLoading
    ) {
      return;
    }
    await dispatch(getAllImages(params));
  };
};

export const postImageComment = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.media.isCommentLoading) {
      return;
    }
    const tempId = `temp-${Date.now()}`;
    await dispatch(addImageComment({ ...params, tempId }));
  };
};

export const getComments = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.media.isCommentLoading) {
      return;
    }
    await dispatch(fetchComments(params));
  };
};

export const getCommentsById = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.media.isCommentLoading) {
      return;
    }
    await dispatch(fetchCommentsById(params));
  };
};
export const commentsUpdate = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.media.isCommentLoading) {
      return;
    }
    await dispatch(updateCommentStatus(params));
  };
};

export const getNotifications = (params) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.media.notificationsLoading) {
      return;
    }
    await dispatch(fetchNotifications(params));
  };
};
